import { useEffect } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { Earth, FileText, type LucideIcon } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { Header } from '@/components/layout/Header';
import { MyTaskList } from '@/components/home/MyTaskList';
import type { Role } from '@/types';
import { AssignedTasksPanel } from '@/components/home/AssignedTasksPanel';
import { ApercuActivite } from '@/components/home/ApercuActivite';

/**
 * Écrans encore indisponibles.
 *
 * Cette page sert de repli pour les seules routes qui n'ont pas encore leur
 * écran : `/reporting/*` et les sous-routes `/holding/*` (voir App.tsx). Les
 * autres entrées de cette table ont été retirées le 19/09/2026 : elles
 * décrivaient des pages livrées depuis longtemps — « Dashboard Finance, Jour 3
 * à venir » — et n'étaient plus jamais atteintes. Du code mort qui annonçait
 * faux.
 *
 * On dit ce qui n'existe pas encore, et surtout ce que l'on peut faire en
 * attendant : les exports PDF et Excel demandés dans « Rapports » sont déjà
 * disponibles depuis le tableau de bord, les factures et le journal.
 */
interface RouteInfo {
  icon:     LucideIcon;
  title:    string;
  subtitle: string;
  /**
   * Ce qui est possible dès aujourd'hui, avec l'écran qui le permet.
   * `roles` restreint la proposition : inutile — et trompeur — d'orienter vers
   * un écran que le rôle ne peut pas ouvrir, il serait redirigé sans explication.
   */
  enAttendant?: { texte: string; lien: string; libelleLien: string; roles?: Role[] };
  /** Ce que cette section apportera. */
  prevu: string[];
}

const ROUTE_INFO: Record<string, RouteInfo> = {
  '/reporting': {
    icon:     FileText,
    title:    'Rapports',
    subtitle: "Cette section n'est pas encore disponible.",
    enAttendant: {
      texte:       'Les exports PDF et Excel sont déjà accessibles depuis les écrans financiers.',
      lien:        '/finance',
      libelleLien: 'Exporter depuis le tableau de bord',
    },
    prevu: [
      'Rapports mensuels, trimestriels et annuels',
      'Choix de la filiale et de la période',
      'Historique des rapports générés',
    ],
  },
  '/holding': {
    icon:     Earth,
    title:    'Vue consolidée du groupe',
    subtitle: "Cette sous-section n'est pas encore disponible.",
    enAttendant: {
      texte:       'La vue consolidée des filiales est déjà accessible.',
      lien:        '/holding',
      libelleLien: 'Ouvrir la vue consolidée',
      roles:       ['admin_holding', 'auditeur'],
    },
    prevu: [
      'Alertes business par filiale',
      'Rapports consolidés',
      'Paramètres du groupe',
    ],
  },
};

const findRouteInfo = (pathname: string): RouteInfo | null => {
  if (ROUTE_INFO[pathname]) return ROUTE_INFO[pathname];
  const segments = pathname.split('/').filter(Boolean);
  for (let i = segments.length; i > 0; i--) {
    const candidate = '/' + segments.slice(0, i).join('/');
    if (ROUTE_INFO[candidate]) return ROUTE_INFO[candidate];
  }
  return null;
};

export const HomePage = () => {
  const { user, filiale, loadFiliale } = useAuthStore();
  const location = useLocation();
  const info = findRouteInfo(location.pathname);

  useEffect(() => {
    if (!filiale) void loadFiliale();
  }, [filiale, loadFiliale]);

  // SIMG-COMMERCIAL/ACHATS-LEADBETON — les rôles dédiés atterrissent sur leur
  // portail mobile-first, jamais sur la HomePage admin (qui déclenche des
  // 403 sur /me/colleagues et affiche des blocs non pertinents).
  if (user?.role === 'commercial') return <Navigate to="/commercial" replace />;
  if (user?.role === 'acheteur')   return <Navigate to="/achats" replace />;

  // Prénom : 2e mot du fullName (convention africaine "Nom Prénom"), fallback 1er
  const firstName = user?.fullName.split(' ')[1] ?? user?.fullName.split(' ')[0] ?? '';
  const isHome    = location.pathname === '/';

  return (
    <>
      <Header
        title={isHome ? `Bienvenue, ${firstName}` : info?.title ?? 'Bientôt disponible'}
        subtitle={
          isHome
            ? `${filiale?.fullName ?? ''} · ${filiale?.metierLabel ?? ''}`
            : info?.subtitle ?? "Cet écran n'est pas encore disponible."
        }
      />

      <div className="p-6 space-y-6">
        {/* Écran pas encore disponible. On annonce ce qui manque, et surtout ce
            que l'on peut faire dès maintenant : l'ancien bandeau se contentait
            d'afficher « Jour 3 — à venir », une référence au planning interne
            qui ne disait rien à l'utilisateur. */}
        {!isHome && info && (
          <div className="rounded-xl border p-6" style={{ borderColor: 'var(--bdr)', background: '#fff' }}>
            <div className="flex items-start gap-4">
              <div
                className="flex size-12 shrink-0 items-center justify-center rounded-lg"
                style={{ background: 'var(--p-soft)', color: 'var(--p)' }}
              >
                <info.icon className="size-6" />
              </div>
              <div className="flex-1">
                {info.enAttendant && (!info.enAttendant.roles || info.enAttendant.roles.includes(user?.role as Role)) && (
                  <div
                    className="flex flex-wrap items-center gap-3 rounded-lg p-3"
                    style={{ background: 'var(--p-tint)' }}
                  >
                    <span className="text-sm" style={{ color: 'var(--tx-2)' }}>{info.enAttendant.texte}</span>
                    <Link
                      to={info.enAttendant.lien}
                      className="inline-flex items-center gap-1 text-sm font-semibold"
                      style={{ color: 'var(--p)' }}
                    >
                      {info.enAttendant.libelleLien} →
                    </Link>
                  </div>
                )}

                <p className="mt-4 text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--tx-3)' }}>
                  Ce que cette section apportera
                </p>
                <ul className="mt-2 space-y-1">
                  {info.prevu.map((f) => (
                    <li key={f} className="text-sm" style={{ color: 'var(--tx-2)' }}>· {f}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Ce qui demande une décision aujourd'hui.
            Remplace trois cartes de métadonnées (pays, devise, métier, e-mail,
            rôle) que l'utilisateur connaissait déjà et qui figurent par ailleurs
            dans l'en-tête et la barre latérale — dont une dont le contenu était
            « Navigation à gauche ». */}
        {isHome && <ApercuActivite />}

        {/* TO DO LIST personnelle + Tâches attribuées à mon équipe (grid 2 cols
            si rôle hiérarchique, sinon juste MyTaskList sur toute la largeur) */}
        {isHome && (
          <div className="ds-mg">
            <MyTaskList />
            <AssignedTasksPanel />
          </div>
        )}
      </div>
    </>
  );
};
